import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ArrowRight, Library, Scale, Globe2, BookOpen } from "lucide-react";
import CountryFlagGrid from "../components/CountryFlagGrid";
import { RIGHTS_TOPICS, TOPIC_SLUGS } from "../data/rightsTopics";

export default function Dashboard() {
  const { user } = useAuth();
  const [savedAgencies, setSavedAgencies] = useState([]);
  const [connections, setConnections] = useState([]);
  const [pending, setPending] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [a, c] = await Promise.all([
          api.get("/agencies/saved/list"),
          api.get("/connections"),
        ]);
        setSavedAgencies(a.data);
        setConnections(c.data.filter((x) => x.status === "accepted"));
        setPending(c.data.filter((x) => x.status === "pending" && x.direction === "incoming"));
      } catch {
        /* ignore */
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A1128] pt-28 pb-20" data-testid="dashboard-page">
      {/* ============ Welcome ============ */}
      <div className="max-w-7xl mx-auto px-6">
        <div className="overline">Welcome back</div>
        <h1 className="font-display text-4xl lg:text-6xl mt-2 tracking-tight">
          {user?.name?.split(" ")[0]}&apos;s registry
        </h1>
        <p className="text-slate-300 mt-4 max-w-3xl leading-relaxed">
          Publishing and international royalty law are knotted disciplines — written by lawyers,
          shaped by treaties, and interpreted by every territory differently. OSRR exists to make
          the knot practical: who collects what, where, when, and how to reach them.
        </p>

        <div className="grid md:grid-cols-3 gap-4 mt-10">
          <Stat label="Favorite agencies" value={savedAgencies.length} />
          <Stat label="Connections" value={connections.length} />
          <Stat label="Pending invites" value={pending.length} accent />
        </div>
      </div>

      {/* ============ Rights: practical philosophy ============ */}
      <section className="max-w-7xl mx-auto px-6 mt-20" data-testid="rights-philosophy">
        <div className="flex items-start gap-4 mb-8">
          <div className="w-12 h-12 rounded-md border border-[#7DA3D9]/30 bg-[#7DA3D9]/5 flex items-center justify-center flex-shrink-0">
            <Scale className="w-5 h-5 text-[#7DA3D9]" />
          </div>
          <div>
            <div className="overline" style={{ color: "#7DA3D9" }}>How rights actually work</div>
            <h2 className="font-display text-3xl lg:text-4xl tracking-tight mt-1">
              The practical philosophy of music rights
            </h2>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl">
          <p className="text-slate-300 leading-relaxed">
            Every piece of recorded music carries <span className="text-white font-medium">two
            separate copyrights</span> — one for the <em>composition</em> (the song itself: melody,
            lyrics, structure) and one for the <em>master</em> (the specific recording). They are
            owned, licensed, and paid out independently. Understanding that split is the foundation
            of every royalty conversation you will ever have.
          </p>
          <p className="text-slate-300 leading-relaxed">
            Around those two copyrights orbit the collecting societies — <span className="text-white font-medium">
            PROs, MROs, neighbouring-rights bodies, sync agents, and master-rights collectives</span> —
            each chartered by a different government, each with its own membership rules, splits,
            and reciprocal agreements. The directory below maps that ecosystem so you never have
            to guess who to call when royalties arrive from a country you have never visited.
          </p>
        </div>
      </section>

      {/* ============ Rights Table of Contents (22 topics) ============ */}
      <section className="max-w-7xl mx-auto px-6 mt-20" data-testid="rights-toc">
        <div className="flex items-start justify-between gap-6 mb-10 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 flex items-center justify-center flex-shrink-0">
              <BookOpen className="w-5 h-5 text-[#D4AF37]" />
            </div>
            <div>
              <div className="overline" style={{ color: "#D4AF37" }}>Table of contents</div>
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight mt-1">
                The 22 rights topics, plainly explained
              </h2>
              <p className="text-slate-400 mt-3 max-w-2xl text-sm leading-relaxed">
                Each topic is an encyclopedic page: a clear introduction, key points, and the
                official agencies (government-badged) plus the major private organizations that
                touch that right.
              </p>
            </div>
          </div>
          <Link to="/rights-library" className="btn-ghost-gold inline-flex items-center gap-2 text-sm" data-testid="toc-library">
            Full library <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>

        <ol className="grid md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="rights-toc-list">
          {TOPIC_SLUGS.map((slug, i) => {
            const t = RIGHTS_TOPICS[slug];
            return (
              <li key={slug}>
                <Link
                  to={`/rights-topic/${slug}`}
                  className="group block p-5 rounded-md border border-white/10 hover:border-[#7DA3D9]/40 hover:bg-white/[0.03] transition-colors h-full"
                  data-testid={`toc-${slug}`}
                >
                  <div className="flex items-start gap-3">
                    <span className="font-display text-[#D4AF37] text-sm w-7 flex-shrink-0">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 mb-1.5">
                        {t.overline}
                      </div>
                      <div className="font-display text-lg text-white group-hover:text-[#B8D0EE] transition-colors leading-snug">
                        {t.title}
                      </div>
                    </div>
                  </div>
                </Link>
              </li>
            );
          })}
        </ol>
      </section>

      {/* ============ Country Flag Grid (160+ countries) ============ */}
      <section className="mt-20 border-y border-white/5 bg-white/[0.012]" data-testid="dashboard-flags">
        <CountryFlagGrid />
      </section>

      {/* ============ Saved + Pending (personal workspace) ============ */}
      <section className="max-w-7xl mx-auto px-6 mt-20">
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="glass-card p-7 lg:col-span-2" data-testid="dashboard-favorites">
            <div className="flex items-center justify-between">
              <div>
                <div className="overline">Your favorites</div>
                <h2 className="font-display text-2xl mt-1">Favorite agencies</h2>
              </div>
              <Link to="/agencies" className="text-[#7DA3D9] text-sm hover:text-[#B8D0EE] inline-flex items-center gap-1" data-testid="dashboard-browse-agencies">
                Browse <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
            <div className="divider-gold my-5" />
            {savedAgencies.length === 0 ? (
              <div className="text-center py-10">
                <Library className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No agencies saved yet.</p>
                <Link to="/agencies" className="btn-ghost-gold inline-flex items-center gap-2 mt-4 text-sm">
                  Open the registry
                </Link>
              </div>
            ) : (
              <div className="space-y-3">
                {savedAgencies.map((a) => (
                  <Link
                    to={`/agencies/${a.id}`}
                    key={a.id}
                    className="flex items-center gap-4 p-3 rounded-lg hover:bg-white/5 transition-colors"
                    data-testid={`dashboard-saved-${a.id}`}
                  >
                    <div className="text-2xl">{a.flag}</div>
                    <div className="flex-1">
                      <div className="font-display text-lg">{a.name}</div>
                      <div className="text-xs text-slate-400">{a.country}</div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-600" />
                  </Link>
                ))}
              </div>
            )}
          </div>

          <div className="glass-card p-7" data-testid="dashboard-pending">
            <div className="overline">Awaiting</div>
            <h2 className="font-display text-2xl mt-1">Pending invites</h2>
            <div className="divider-gold my-5" />
            {pending.length === 0 ? (
              <div className="text-center py-8">
                <Globe2 className="w-8 h-8 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No pending invitations.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pending.slice(0, 5).map((c) => (
                  <div key={c.id} className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="font-medium">{c.other_user?.name}</div>
                    <div className="text-xs text-slate-400 mt-0.5">{c.other_user?.role} · {c.other_user?.country}</div>
                  </div>
                ))}
                <Link to="/connections" className="block text-center text-sm text-[#7DA3D9] hover:text-[#B8D0EE] mt-4" data-testid="view-all-invites">
                  Manage all →
                </Link>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="glass-card p-6">
      <div className="overline">{label}</div>
      <div className={`font-display text-5xl mt-2 ${accent ? "text-[#7DA3D9]" : "text-white"}`}>{value}</div>
    </div>
  );
}
